import {
    type NavGroup,
    NavItemType,
    type IShowroomBusinessEntityConfig,
    type INavViewItem
} from '@makemydeal/dr-sales-checkin-types';

import { navItemRegistry, iconByKey, badgeByKey, labelByKey } from './navItemRegistry';
import { cmd, commandBuildersByKey } from './commandBuilders';
import { isNavItemHidden, isNavItemDisabled } from './navRules';
import type { CommandBuilder, NavViewContext } from '../../types/showroomNavTypes';

/**
 * Turns one serializable entity into a render-ready {@link INavViewItem} by
 * overlaying static registry metadata, runtime data from context and the resolved
 * commands. Every command folds the entity's disable rule into its `canExecute`.
 * Returns `null` for entities with no registry entry.
 */
const assembleViewItem = (entity: IShowroomBusinessEntityConfig, ctx: NavViewContext): INavViewItem | null => {
    // 1. Static presentation for this key (type/title/icon/order/path…). No registry
    //    entry means the key isn't renderable, so bail — the caller filters these out.
    const pres = navItemRegistry[entity.key];
    if (!pres) {
        return null;
    }

    // 2. Everything shared by every view item, spread into each case below: identity and
    //    layout, plus the presentation the base type carries — `label` (a runtime reader
    //    from `labelByKey` for the customer names, else the static title), the resolved
    //    icon and the tooltip. Only genuinely per-kind fields remain in the switch.
    const base = {
        key: entity.key,
        groupBy: entity.groupBy,
        order: pres.order,
        placement: pres.placement ?? undefined,
        label: labelByKey[entity.key]?.(ctx) ?? pres.title,
        icon: pres.iconKey ? iconByKey[pres.iconKey] : undefined,
        tooltip: pres.tooltip
    };

    // 3. The command builder(s) for this key (click / add / remove). May be empty for
    //    display-only items (e.g. ShareDropdown), hence the `?? {}` fallback.
    const builders = commandBuildersByKey[entity.key] ?? {};

    // 4. Runtime badge, if this key declares one in `badgeByKey` (today only recentDeals,
    //    reading the live deal count from the customerVehicles slice via ctx). Keeping it
    //    in the registry map means the pipeline never names a business entity.
    const badge = badgeByKey[entity.key]?.(ctx);

    // 5. Resolve one of those builders into a command, folding in the disable rule:
    //    `isNavItemDisabled` runs the navRules `isDisabled` rule and when true we hand
    //    back `cmd(false)` (a no-op the component renders as disabled) instead of the
    //    builder's real command. This is how Card/Link items (side-nav + share dropdown)
    //    grey out. The `!` is safe — the registry only lists kinds whose builders exist.
    const command = (builder?: CommandBuilder) => (isNavItemDisabled(entity, ctx) ? cmd(false) : builder!(ctx));

    // 6. Build the render-ready item per kind. The discriminated union on `type` means
    //    each case returns exactly the segregated config its component needs.
    switch (pres.type) {
        // Buyer name — clickable, opens customer info.
        case NavItemType.CustomerName:
            return { ...base, type: NavItemType.CustomerName, clickAction: command(builders.click) };
        // Co-buyer — clickable, plus a remove command behind the trash icon.
        case NavItemType.Cobuyer:
            return {
                ...base,
                type: NavItemType.Cobuyer,
                clickAction: command(builders.click),
                removeAction: command(builders.remove)
            };
        // Add-Co-Buyer CTA — an `add` command behind the AddIcon.
        case NavItemType.AddCobuyer:
            return { ...base, type: NavItemType.AddCobuyer, addAction: command(builders.add) };
        // Inbox icon (buyer or co-buyer) — click opens the inbox.
        case NavItemType.Inbox:
            return { ...base, type: NavItemType.Inbox, clickAction: command(builders.click) };
        // Send to Desk — the button owns the send itself through `useSendToDesk`, which also
        // gives it its pending state, so there is no command to fold the disable rule into;
        // `isDisabled` carries the rule's verdict for the button to honour.
        case NavItemType.SendToDesk:
            return { ...base, type: NavItemType.SendToDesk, isDisabled: isNavItemDisabled(entity, ctx) };
        // Share button — container; the hook injects its menu items (the DROPDOWN entities).
        case NavItemType.ShareDropdown:
            return { ...base, type: NavItemType.ShareDropdown, items: [] };
        // Deal Comments — always visible, no click command; the component owns its own open/close state.
        case NavItemType.DealComments:
            return { ...base, type: NavItemType.DealComments };
        // Left-nav card — description + active state from the resolved active section.
        case NavItemType.Card:
            return {
                ...base,
                type: NavItemType.Card,
                description: pres.description,
                isActive: ctx.activeSectionKey === entity.key,
                clickAction: command(builders.click)
            };
        // Link — quick links + share-dropdown items; only the badge is kind-specific.
        case NavItemType.Link:
            return { ...base, type: NavItemType.Link, badge, clickAction: command(builders.click) };
        /* istanbul ignore next -- exhaustive switch; registry types are all handled above */
        default:
            return null;
    }
};

/**
 * The single assembly pipeline both nav hooks share: pick the entities for a
 * group, drop hidden ones (config flag AND rule engine), resolve each to a view
 * item, then sort by registry order.
 */
export const buildNavViewItems = (
    entities: IShowroomBusinessEntityConfig[],
    group: NavGroup,
    ctx: NavViewContext
): INavViewItem[] =>
    entities
        .filter((entity) => ctx.isCapabilityVisible(entity.key))
        .filter((entity) => entity.groupBy === group && entity.isVisible && !isNavItemHidden(entity, ctx))
        .map((entity) => assembleViewItem(entity, ctx))
        .filter((item): item is INavViewItem => item !== null)
        .sort((a, b) => a.order - b.order);
